import { Flex, Heading } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import {
	IoBagCheckOutline,
	IoBusinessOutline,
	IoCartOutline,
	IoClipboardOutline,
	IoHomeOutline,
	IoLogOutOutline,
	IoMailOutline,
	IoMegaphoneOutline,
	IoPeopleOutline,
	IoPhonePortraitOutline,
	IoSendOutline,
} from 'react-icons/io5';
import { useLocation, useNavigate } from 'react-router';

import SidebarItem from './SuperadminSidebarItem';
import { LanguageSelector } from '../SuperadminLanguageSelector';
import { useSuperadminAuth } from '../../context/SuperadminAuthContext';
import { ConfirmModal, type ConfirmModalHandle } from '@shared';
import { useRef } from 'react';
import { MdOutlineAdminPanelSettings } from 'react-icons/md';

export default function Sidebar() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const { clearAuthData } = useSuperadminAuth();

	const handleLogOutClick = async () => {
		const response = await waitForUserResponseOnLogOut.current?.open({
			title: t('general.log_out'),
			content: t('general.really_log_out'),
			confirmButtonText: t('general.log_out'),
			confirmButtonVariant: 'subtle',
		});
		if (response === false) return;
		clearAuthData();
	};

	const sidebarIconSize = 30;
	const sidebarItems = [
		{
			icon: <IoHomeOutline size={sidebarIconSize} />,
			label: t('general.overview'),
			path: '/overview',
			onClick: () => navigate(`/overview`),
		},
		{
			icon: <IoMailOutline size={sidebarIconSize} />,
			label: t('general.sms_messages'),
			path: '/sms-messages',
			onClick: () => navigate(`/sms-messages`),
		},
		{
			icon: <IoBusinessOutline size={sidebarIconSize} />,
			label: t('general.accounts'),
			path: '/accounts',
			onClick: () => navigate(`/accounts`),
		},
		{
			icon: <IoPeopleOutline size={sidebarIconSize} />,
			label: t('general.users'),
			path: '/users',
			onClick: () => navigate(`/users`),
		},
		{
			icon: <MdOutlineAdminPanelSettings size={sidebarIconSize} />,
			label: t('general.superadmins'),
			path: '/superadmins',
			onClick: () => navigate(`/superadmins`),
		},
		{
			icon: <IoCartOutline size={sidebarIconSize} />,
			label: t('general.orders'),
			path: '/orders',
			onClick: () => navigate(`/orders`),
		},
		{
			icon: <IoBagCheckOutline size={sidebarIconSize} />,
			label: t('general.discount_codes'),
			path: '/discount-codes',
			onClick: () => navigate(`/discount-codes`),
		},
		{
			icon: <IoMegaphoneOutline size={sidebarIconSize} />,
			label: t('general.announcements'),
			path: '/announcements',
			onClick: () => navigate(`/announcements`),
		},
		{
			icon: <IoSendOutline size={sidebarIconSize} />,
			label: t('general.sender_names'),
			path: '/sender-names',
			onClick: () => navigate(`/sender-names`),
		},
		{
			icon: <IoPhonePortraitOutline size={sidebarIconSize} />,
			label: t('general.inbound_numbers'),
			path: '/inbound-numbers',
			onClick: () => navigate(`/inbound-numbers`),
		},
		{
			icon: <IoClipboardOutline size={sidebarIconSize} />,
			label: t('general.surveys'),
			path: '/surveys',
			onClick: () => navigate(`/surveys`),
		},
	];

	const waitForUserResponseOnLogOut = useRef<ConfirmModalHandle>(null);

	return (
		<Flex
			flexDirection="column"
			width={'200px'}
			height={'100%'}
			backgroundColor={'rgba(251, 249, 255, 1)'}
			zIndex={3}
			overflow={'auto'}
			boxShadow={'0 4px 6px rgba(0, 0, 0, 0.1),  0 1px 3px rgba(0, 0, 0, 0.08)'}
		>
			<Flex
				marginBottom={2}
				flexDirection={'column'}
				alignItems={'center'}
				justifyContent="center"
				color="white"
				backgroundColor={'rgb(61, 213, 152)'}
				width={'100%'}
				height={'100px'}
				padding={2}
			>
				<Heading>Tavoittaja</Heading>
				<Heading>{t('general.superadmin')}</Heading>
			</Flex>
			{sidebarItems.map(item => (
				<SidebarItem path={item.path} isSelected={pathname.includes(item.path)} key={item.label} icon={item.icon} label={item.label} actionOnClick={item.onClick} />
			))}
			<SidebarItem
				path={'/log-out'}
				isSelected={pathname.includes('/log-out')}
				icon={<IoLogOutOutline size={sidebarIconSize} />}
				label={t('general.log_out')}
				actionOnClick={handleLogOutClick}
			/>

			<Flex marginTop={'auto'} justifyContent={'center'} flexDirection={'column'}>
				<Flex marginBottom={2} mt={2} justifyContent={'center'}>
					<LanguageSelector />
					<ConfirmModal ref={waitForUserResponseOnLogOut} />
				</Flex>
			</Flex>
		</Flex>
	);
}
